/**
 * Color Extractor Utility
 * Extracts dominant color from images using Canvas API
 */

/**
 * Extract the dominant color from an image URL
 * @param {string} imageUrl - URL of the image to analyze
 * @returns {Promise<string>} - Hex color code (e.g., "#FF5500")
 */
export const extractDominantColor = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous"; // Enable CORS for external images
    
    img.onload = () => {
      try {
        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Use smaller size for faster processing
        const maxSize = 100;
        const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Get pixel data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        
        // Color frequency map
        const colorCounts = {};
        
        // Analyze pixels (skip every 4th pixel for speed)
        for (let i = 0; i < pixels.length; i += 16) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const a = pixels[i + 3];
          
          // Skip transparent pixels
          if (a < 128) continue;
          
          // Skip near-white pixels (backgrounds)
          if (r > 240 && g > 240 && b > 240) continue;
          
          // Skip near-black pixels (backgrounds/outlines)
          if (r < 15 && g < 15 && b < 15) continue;
          
          // Skip grayscale pixels (likely backgrounds)
          const isGray = Math.abs(r - g) < 20 && Math.abs(g - b) < 20 && Math.abs(r - b) < 20;
          if (isGray && r > 100 && r < 200) continue;
          
          // Quantize colors to reduce noise (group similar colors)
          const qr = Math.round(r / 16) * 16;
          const qg = Math.round(g / 16) * 16;
          const qb = Math.round(b / 16) * 16;
          
          const key = `${qr},${qg},${qb}`;
          
          // Weight by saturation (more saturated = more likely team color)
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          const weight = 1 + saturation * 2; // Boost saturated colors
          
          colorCounts[key] = (colorCounts[key] || 0) + weight;
        }
        
        // Find most frequent color
        let maxCount = 0;
        let dominantColor = [128, 128, 128]; // Default gray
        
        for (const [key, count] of Object.entries(colorCounts)) {
          if (count > maxCount) {
            maxCount = count;
            dominantColor = key.split(',').map(Number);
          }
        }
        
        // Convert to hex
        const hex = rgbToHex(dominantColor[0], dominantColor[1], dominantColor[2]);
        resolve(hex);
        
      } catch (error) {
        reject(new Error('Failed to analyze image colors'));
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image. Try a different image URL or use manual color selection.'));
    };
    
    // Add cache-busting for CORS issues
    const separator = imageUrl.includes('?') ? '&' : '?';
    img.src = `${imageUrl}${separator}t=${Date.now()}`;
  });
};

/**
 * Convert RGB values to hex color
 */
const rgbToHex = (r, g, b) => {
  const toHex = (n) => {
    const hex = Math.min(255, Math.max(0, n)).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

/**
 * Extract multiple colors from an image (primary and secondary)
 * @param {string} imageUrl - URL of the image to analyze
 * @returns {Promise<{primary: string, secondary: string}>}
 */
export const extractColors = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const maxSize = 100;
        const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        
        const colorCounts = {};
        
        for (let i = 0; i < pixels.length; i += 16) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const a = pixels[i + 3];
          
          if (a < 128) continue;
          if (r > 240 && g > 240 && b > 240) continue;
          if (r < 15 && g < 15 && b < 15) continue;
          
          const isGray = Math.abs(r - g) < 20 && Math.abs(g - b) < 20 && Math.abs(r - b) < 20;
          if (isGray && r > 100 && r < 200) continue;
          
          const qr = Math.round(r / 24) * 24;
          const qg = Math.round(g / 24) * 24;
          const qb = Math.round(b / 24) * 24;
          
          const key = `${qr},${qg},${qb}`;
          
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          const weight = 1 + saturation * 2;
          
          colorCounts[key] = (colorCounts[key] || 0) + weight;
        }
        
        // Sort colors by frequency
        const sortedColors = Object.entries(colorCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 2);
        
        const primary = sortedColors[0] 
          ? rgbToHex(...sortedColors[0][0].split(',').map(Number))
          : '#666666';
          
        const secondary = sortedColors[1]
          ? rgbToHex(...sortedColors[1][0].split(',').map(Number))
          : '#FFFFFF';
        
        resolve({ primary, secondary });
        
      } catch (error) {
        reject(new Error('Failed to analyze image colors'));
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    const separator = imageUrl.includes('?') ? '&' : '?';
    img.src = `${imageUrl}${separator}t=${Date.now()}`;
  });
};

export default extractDominantColor;
